import type { CdnPackage, CdnPackages, CdnQuota } from '../types/cdn';
import type { TrialConfig } from '../types/trial';
import { cdnQuotaState } from '../utils/cdnQuota';
import { formatBytes, formatGb } from '../utils/formatBytes';
import { formatPrice } from '../utils/formatPrice';
import { formatDays } from '../utils/pluralize';

/*
 * Все пользовательские тексты про обход белых списков (CDN-контур) собраны здесь,
 * чтобы формулировку можно было поменять в одном месте — компоненты только импортируют.
 * Числа (квоты, цены) всегда приходят из API, тут только шаблоны.
 */

/* ===== Блок квоты на дашборде ===== */

export const CDN_TITLE = 'Обход белых списков';

/** Подпись под баром в обычном состоянии */
export const CDN_USAGE_HINT =
  'Трафик считается только по обходу белых списков. Остальные серверы — без ограничений.';

/** Квота исчерпана, докупить можно */
export const CDN_EXHAUSTED_HINT =
  'Лимит на обход белых списков закончился — докупите трафик, чтобы продолжить.';

/** Квота исчерпана, докупить нельзя (триал) */
export const CDN_EXHAUSTED_NO_TOPUP = 'Лимит на обход белых списков исчерпан';

export const CDN_TRIAL_HINT = 'В пробном периоде докупка недоступна.';

/** percent_used ≥ warn_percent, но ещё не исчерпано */
export function cdnWarnHint(warnPercent: number): string {
  return `Осталось меньше ${100 - warnPercent}% — можно докупить заранее.`;
}

/** Подпись под баром квоты по её состоянию (карточка подписки) */
export function cdnQuotaHint(q: CdnQuota): string {
  switch (cdnQuotaState(q)) {
    case 'exhausted':
      if (q.topupAvailable) return CDN_EXHAUSTED_HINT;
      return q.topupUnavailableReason === 'trial'
        ? `${CDN_EXHAUSTED_NO_TOPUP}. ${CDN_TRIAL_HINT}`
        : `${CDN_EXHAUSTED_NO_TOPUP}.`;
    case 'warning':
      // Без докупки «можно докупить заранее» вводило бы в заблуждение
      return q.topupAvailable ? cdnWarnHint(q.warnPercent) : `Осталось меньше ${100 - q.warnPercent}%.`;
    default:
      return q.source === 'trial' ? CDN_TRIAL_HINT : CDN_USAGE_HINT;
  }
}

/** «2,1 ГБ из 7,0 ГБ» */
export function cdnUsageShort(q: CdnQuota): string {
  return `${formatBytes(q.usedBytes)} из ${formatBytes(q.totalBytes)}`;
}

/** «2,1 ГБ из 7,0 ГБ · осталось 4,9 ГБ» */
export function cdnUsageLine(q: CdnQuota): string {
  return `${cdnUsageShort(q)} · осталось ${formatBytes(q.availableBytes)}`;
}

/* ===== Докупка ===== */

export const CDN_TOPUP_BUTTON = 'Докупить';
export const CDN_TOPUP_MODAL_TITLE = 'Докупить трафик для обхода белых списков';
export const CDN_TOPUP_MODAL_HINT =
  'Пакет добавится к вашей квоте сразу после оплаты. Трафик на обычных серверах не ограничен.';
export const CDN_TOPUP_PACKAGES_LABEL = 'Выберите пакет';
export const CDN_TOPUP_PACKAGES_UNAVAILABLE = 'Пакеты временно недоступны. Попробуйте позже';
export const CDN_TOPUP_SUCCESS = 'Трафик для обхода белых списков оплачен и добавлен к квоте';
export const CDN_TOPUP_PROMO_DAYS_ERROR = 'Промокод с бонусными днями нельзя применить к докупке трафика';

/** «10 ₽/ГБ» — цена гигабайта внутри пакета */
export function cdnPackagePerGb(pkg: CdnPackage): string {
  if (pkg.gb <= 0) return '';
  return `${formatPrice(Math.round(pkg.priceKopecks / pkg.gb))}/ГБ`;
}

/** «5 ГБ — 50 ₽ (10 ₽/ГБ)» */
export function cdnPackageLabel(pkg: CdnPackage): string {
  return `${pkg.gb} ГБ — ${formatPrice(pkg.priceKopecks)} (${cdnPackagePerGb(pkg)})`;
}

/**
 * Строка на странице тарифов, собирается из /cdn/packages:
 * «Докупка трафика для обхода белых списков — 10 ₽/ГБ · пакеты: 5 ГБ — 50 ₽, 10 ГБ — 90 ₽, …»
 */
export function cdnPackagesLine(p: CdnPackages): string {
  const list = p.packages.map((pkg) => `${pkg.gb} ГБ — ${formatPrice(pkg.priceKopecks)}`).join(', ');
  const perGb = p.pricePerGbKopecks > 0 ? ` — ${formatPrice(p.pricePerGbKopecks)}/ГБ` : '';
  const packages = list ? ` · пакеты: ${list}` : '';
  return `Докупка трафика для обхода белых списков${perGb}${packages}`;
}

/* ===== Тарифы ===== */

export const PLANS_SUBTITLE =
  'Все тарифы включают все серверы, безлимитный трафик на обычных серверах и пакет трафика для обхода белых списков';

export const PLAN_FEATURE_UNLIMITED = 'Безлимитный трафик на обычных серверах';

/** «7 ГБ/мес» — значение выделенной фичи тарифа */
export function cdnPlanFeatureValue(bytesPerMonth: number): string {
  return `${formatGb(bytesPerMonth)}/мес`;
}

/** «Обход белых списков: 7 ГБ/мес» */
export function cdnPlanFeature(bytesPerMonth: number): string {
  return `${CDN_TITLE}: ${cdnPlanFeatureValue(bytesPerMonth)}`;
}

/** «Доп. ключ: 100 ₽», при нулевой цене — «Доп. ключи недоступны» */
export function extraKeyFeature(priceKopecks: number): string {
  return priceKopecks > 0 ? `Доп. ключ: ${formatPrice(priceKopecks)}` : 'Доп. ключи недоступны';
}

/* ===== Пробный период ===== */

/** «7 дней, 7 ГБ трафика, 1 ГБ на обход белых списков — без оплаты и привязки карты.» */
export function trialInviteHint(c: TrialConfig): string {
  const parts = [formatDays(c.durationDays)];
  if (c.trafficLimitBytes > 0) parts.push(`${formatGb(c.trafficLimitBytes)} трафика`);
  if (c.cdnTrafficLimitBytes > 0) parts.push(`${formatGb(c.cdnTrafficLimitBytes)} на обход белых списков`);
  return `${parts.join(', ')} — без оплаты и привязки карты.`;
}

/** Пока /trial/config не загрузился — текст без чисел, чтобы не показать устаревшие */
export const TRIAL_INVITE_FALLBACK = 'Бесплатный пробный период — без оплаты и привязки карты.';

/** Ответ в FAQ на лендинге; без конфига — общая формулировка без чисел */
export function trialFaqAnswer(c: TrialConfig | null): string {
  const tail = 'За это время вы сможете оценить скорость и стабильность подключения.';
  if (!c) return `Да! При регистрации вы получаете бесплатный пробный доступ. ${tail}`;
  const limits: string[] = [];
  if (c.trafficLimitBytes > 0) limits.push(`${formatGb(c.trafficLimitBytes)} трафика`);
  if (c.cdnTrafficLimitBytes > 0) limits.push(`${formatGb(c.cdnTrafficLimitBytes)} на обход белых списков`);
  const inBrackets = limits.length ? ` (${limits.join(', ')})` : '';
  return `Да! При регистрации вы получаете бесплатный пробный доступ на ${formatDays(c.durationDays)}${inBrackets}. ${tail}`;
}
